import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";

import { Grid } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_VISITA_DATOS_CLINICOS_MEDICO, { adaptAddVisitaDatoClinicoInput } from "../../../../../../data/graphQL/mutations/Visitas/saveDatosClinicosMedico";
import { useStepSave } from "../../../../hooks/useStepSave";
import { validateStepFields } from "../../../../utils/visitaStepValidation";
import CardBox from "../../CardBox/CardBox";
import { SearchDiagnosticoCIE } from "../../../../../ResumenIngresoCreation/components/StepDatosGenerales/components/SearchDiagnosticoCIE";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { getRoundedNumber } from "../../../../../../utils/mathUtils";
import { CALCULAR_Z_SCORES } from "../../../../../../data/graphQL/queries/ResumenIngreso/calcularZScores";
import { getEdad } from "../../../../../../adapters/resumenIngreso/datosFiliatoriosAdapter";
import { shouldIncludeInput } from "../../../../utils/auxiliaryFunctions";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { GET_ALL_ALERGIAS } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllAlergias";
import { adaptAlergiaArray } from "../../../../../../adapters/alergiaAdapter";

export default function DatosClinicos({
  readOnly,
  onSaveRef,
  esUrgencia,
  onExposeFormatted
}) {
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();
  const [alergiasOptions, setAlergiasOptions] = useState([]);

  const { visita, setVisita } = useVisita();

  const { esAdulto, esPediatrico } = useMemo(() => ({
    esAdulto: visita.edad > 15,
    esPediatrico: visita.edad <= 15
  }), [visita.edad]);

  const { requiereSoporte } = useMemo(() => ({
    requiereSoporte: (visita.programa == "AE" || visita.programa == "AP" || visita.programa == "CEAE")
  }), [visita.programa]);

  const inputCombos = inputs.
    filter((input) => {
      if (input.esAdulto && esPediatrico) return false;
      if (input.esPediatrico && esAdulto) return false;
      return true;
    }).
    filter((input) => !!input.comboName);

  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: 'no-cache',
    skip: !esAdulto && !esPediatrico
  });

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI));
  }, [dataCombosRI]);

  const { data: alergiasData } = useQuery(GET_ALL_ALERGIAS, {
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (!alergiasData) return;
    const newAlergias = adaptAlergiaArray(alergiasData);
    setAlergiasOptions(newAlergias);
  }, [alergiasData]);

  const alergiaOtrosId = useMemo(() => {
    const otrosId = alergiasOptions.find((x) => x.displayName.toLowerCase() === 'otros')?.id || -1;
    return otrosId;
  }, [alergiasOptions]);

  useEffect(() => {
    if (!alergiasOptions) return;

    const opcionNo = alergiasOptions.find((x) => x.displayName.includes('NO'));
    const disableCondition = formData.alergiasDatosClinicos?.includes(opcionNo?.id) ?
      (id) => id !== opcionNo?.id :
      (id) => formData.alergiasDatosClinicos?.length ? id === opcionNo?.id : false;

    setAlergiasOptions((prevAlergias) =>
      prevAlergias.map((ant) => ({ ...ant, disabled: disableCondition(ant.id) }))
    );
  }, [formData.alergiasDatosClinicos]);

  const [getCalculosZScores] = useLazyQuery(CALCULAR_Z_SCORES, {
    fetchPolicy: 'no-cache'
  });

  const [save] = useMutation(SAVE_VISITA_DATOS_CLINICOS_MEDICO);

  const valoresIdsFormulas = useMemo(() => {
    if (!combosRI) return {};

    const periodoMayorId = combosRI?.idComboRIValorTiempo?.find((x) => x.displayName.includes('Mayor'))?.id;
    const periodoMenorId = combosRI?.idComboRIValorTiempo?.find((x) => x.displayName.includes('Menor'))?.id;
    const gradoLeveId = combosRI?.idComboRIValorGradoDesnutricion?.find((x) => x.displayName.includes('Leve'))?.id;
    const gradoModeradoId = combosRI?.idComboRIValorGradoDesnutricion?.find((x) => x.displayName.includes('Moderado'))?.id;
    const gradoSeveraId = combosRI?.idComboRIValorGradoDesnutricion?.find((x) => x.displayName.includes('Severa'))?.id;

    return {
      periodoMayorId,
      periodoMenorId,
      gradoLeveId,
      gradoModeradoId,
      gradoSeveraId
    };
  }, [combosRI]);

  const comboDiagnosticoNutricional = useMemo(() => {
    //Calculo, auto seleccionamiento y filtrado de opciones diágnostico nutricional para paciente adulto
    if (!esAdulto || !combosRI) return;
    const { gradoLeveId, gradoModeradoId, gradoSeveraId } = valoresIdsFormulas;
    switch (formData.idComboRIValorGradoDesnutricion) {
      case gradoLeveId:
        const comboValorLeve = combosRI.idComboRIValorDiagnostico.
          filter((x) => x.displayName.includes('Normonutrido') || x.displayName.includes('Riesgo nutricional'));
        // si el valor de diagnóstico es una opción válida para Leve la asigno, sino limpio el valor
        const valorDiag = (comboValorLeve.find(x => x.id == formData.idComboRIValorDiagnostico) != null)?formData.idComboRIValorDiagnostico:null;
        setFormData((prevDatos) => ({
          ...prevDatos,
          idComboRIValorDiagnostico: valorDiag
        }));
        return comboValorLeve;

      case gradoModeradoId:
        const comboValorModerada = combosRI.idComboRIValorDiagnostico.
          filter((x) => x.displayName.includes('Desnutrición Moderada'));
        setFormData((prevDatos) => ({ ...prevDatos, idComboRIValorDiagnostico: comboValorModerada[0].id }));
        return comboValorModerada;
      case gradoSeveraId:
        const comboValorSevera = combosRI.idComboRIValorDiagnostico.
          filter((x) => x.displayName.includes('Desnutrición Severa'));
        setFormData((prevDatos) => ({ ...prevDatos, idComboRIValorDiagnostico: comboValorSevera[0].id }));
        return comboValorSevera;
      default:
        return [];
    }

  }, [
    combosRI,
    esAdulto,
    formData.idComboRIValorGradoDesnutricion,
    formData.idComboRIValorDiagnostico]
  );

  // Fuera del componente
  const calcularRequeridoJustificacionVariacion = (formData, visita) => {
    const tienePesoValido =
      typeof formData.pesoActual === 'number' &&
      typeof visita?.prevPesoActual === 'number' &&
      visita?.prevPesoActual > 0;

    if (!tienePesoValido) return false;

    const diffPercent = Math.abs(formData.pesoActual - visita?.prevPesoActual) / visita?.prevPesoActual * 100;
    return diffPercent > 5;
  };

  // Dentro del componente:
  const formattedInputs = useMemo(() => {
    return inputs.
      filter((input) => {
        if (input.esAdulto && esPediatrico) return false;
        if (input.esPediatrico && esAdulto) return false;
        return true;
      }).
      filter((input) => shouldIncludeInput(input, visita.edad)).
      map((input) => {
        const newInput = { ...input };

        if (!!newInput.comboName) {
          newInput.options = combosRI?.[input.name];
        }

        if (newInput.name === 'justificacionVariacion') {
          const esRequerido = calcularRequeridoJustificacionVariacion(formData, visita);
          if (esRequerido) {
            newInput.required = true;
            newInput.hidden = false;
          }
        }
        if (newInput.name === 'idComboRIValorDiagnostico' && newInput.esAdulto) {
          newInput.options = comboDiagnosticoNutricional;
        }
        if (newInput.name === 'idComboRIValorJustificacion') {
          newInput.required = requiereSoporte;
        }
        if (newInput.name === 'causaJustificacion') {
          newInput.required = formData?.idComboRIValorJustificacion;
          newInput.hidden = !newInput.required;
        }
        if (newInput.name === 'justificacionModificacionDatosClinicos') {
          newInput.required = visita?.prevFasePaciente !== formData?.idComboRIValorFase;
          newInput.hidden = !newInput.required;
        }
        if (newInput.name === 'alergiasDatosClinicos') {
          newInput.options = alergiasOptions;
        }
        if (newInput.name === 'descripcionAlergiaOtro') {
          const hasOtrasAlergias = formData.alergiasDatosClinicos?.includes(alergiaOtrosId);
          newInput.hidden = !hasOtrasAlergias;
          newInput.required = !!hasOtrasAlergias;
        }

        if (readOnly) newInput.disabled = true;
        if (esUrgencia) newInput.required = false;
        return newInput;
      });
  }, [
    readOnly,
    visita.edad,
    formData.pesoActual,
    formData?.idComboRIValorJustificacion,
    formData?.idComboRIValorFase,
    visita?.prevPesoActual,
    esUrgencia,
    comboDiagnosticoNutricional,
    combosRI,
    alergiasOptions
  ]
  );

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedInputs, formData });

    const { data } = await save({
      variables: { input: adaptAddVisitaDatoClinicoInput(formData, alergiaOtrosId) }
    });

    const idVisitaDatoClinico = data?.saveVisitaDatoClinico?.idVisitaDatoClinico;
    if (!idVisitaDatoClinico) throw new Error("Error al guardar");

    setVisita((prev) => ({
      ...prev,
      ...formData,
      idVisitaDatoClinico
    }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(inputs, false);

      setFormData((prev) => ({
        ...prev,
        ...filterVisitaData(visita, inputNames),
        idVisitaDatoClinico: visita.idVisitaDatoClinico,
        idVisita: visita.idVisita,
        idPaciente: visita.idPaciente
      }));

      // setFormData({
      //   ...filterVisitaData(visita, inputNames),
      //   idVisitaDatoClinico: visita.idVisitaDatoClinico,
      //   idVisita: visita.idVisita
      // });
    }
  }, [visita]);

  useEffect(() => {
    //Calculo de IMC y % pérdida de peso para paciente adulto
    if (!esAdulto) return;

    const calcularImc = (pesoActual, talla) =>
      getRoundedNumber(pesoActual / (talla * talla)) || 0;


    const calcularPerdidaPeso = (pesoHab, pesoActual) =>
      getRoundedNumber((pesoHab - pesoActual) / pesoHab * 100) || 0;


    setFormData((prev) => ({
      ...prev,
      iMC: calcularImc(prev.pesoActual, prev.talla),
      perdidaPeso: calcularPerdidaPeso(prev.pesoHabitual, prev.pesoActual)
    }));
  }, [
    esAdulto,
    formData.pesoActual,
    formData.talla,
    formData.pesoHabitual]
  );

  useEffect(() => {
    //Calculo de grado de desnutrición para paciente adulto
    if (!esAdulto) return;
    setFormData((prev) => {
      const { perdidaPeso, idComboRIValorTiempo: periodo } = prev;
      const { periodoMayorId, periodoMenorId, gradoLeveId, gradoModeradoId, gradoSeveraId } = valoresIdsFormulas;

      let idComboRIValorGradoDesnutricion;
      if (perdidaPeso === null || perdidaPeso === undefined || !periodo) {
        idComboRIValorGradoDesnutricion = null;

      } else
        if (perdidaPeso < 5 && periodo === periodoMenorId || perdidaPeso < 10 && periodo === periodoMayorId) {
          idComboRIValorGradoDesnutricion = gradoLeveId;

        } else
          if (perdidaPeso >= 5 && perdidaPeso <= 10 && periodo === periodoMenorId || perdidaPeso >= 10 && perdidaPeso <= 20 && periodo === periodoMayorId) {
            idComboRIValorGradoDesnutricion = gradoModeradoId;

          } else
            if (perdidaPeso > 10 && periodo === periodoMenorId || perdidaPeso > 20 && periodo === periodoMayorId) {
              idComboRIValorGradoDesnutricion = gradoSeveraId;

            }
      return { ...prev, idComboRIValorGradoDesnutricion };
    });
  }, [
    esAdulto,
    formData.perdidaPeso,
    formData.idComboRIValorTiempo,
    valoresIdsFormulas]
  );

  useEffect(() => {
    //Calculo y auto seleccionamiento de Meta del soporte nutricional - adulto
    if (!esAdulto || !combosRI) return;
    setFormData((prev) => {
      const replecionId = combosRI.idComboRIValorSoporte.find((x) => x.displayName.includes('Repleción')).id;
      const mantenimientoId = combosRI.idComboRIValorSoporte.find((x) => x.displayName.includes('Mantenimiento')).id;

      const diagnosticoSeveraId = combosRI.idComboRIValorDiagnostico.find((x) => x.displayName.includes('Desnutrición Severa')).id;
      const diagnosticoModeradaId = combosRI.idComboRIValorDiagnostico.find((x) => x.displayName.includes('Desnutrición Moderada')).id;
      const diagnosticoRiesgoNutriId = combosRI.idComboRIValorDiagnostico.find((x) => x.displayName.includes('Riesgo nutricional')).id;
      const diagnosticoNormunutridoId = combosRI.idComboRIValorDiagnostico.find((x) => x.displayName.includes('Normonutrido')).id;

      let idComboRIValorSoporte = null;

      if (formData.idComboRIValorDiagnostico === diagnosticoSeveraId ||
        formData.idComboRIValorDiagnostico === diagnosticoModeradaId)
        idComboRIValorSoporte = replecionId; else

        if (formData.idComboRIValorDiagnostico === diagnosticoRiesgoNutriId ||
          formData.idComboRIValorDiagnostico === diagnosticoNormunutridoId && esAdulto)
          idComboRIValorSoporte = mantenimientoId;

      return { ...prev, idComboRIValorSoporte };
    });
  }, [
    esAdulto,
    combosRI,
    formData.idComboRIValorDiagnostico]
  );
  useEffect(() => {
    //Calculo de Z Scores para pediátricos
    if (!esPediatrico || !formData.talla || !formData.pesoActual) return;
    const { sexo } = visita;
    const { edadAnios, meses, dia } = getEdad(visita.fechaNacimiento, 'DD/MM/YYYY');

    const getCalculos = async () => {

      const { data } = await getCalculosZScores({
        variables: {
          tallaEnMetros: formData.talla,
          peso: formData.pesoActual,
          edad: edadAnios,
          mes: meses,
          dia: dia,
          sexo
        }
      });

      if (!data) return;

      const { calcularZScores: { zTalla, zPeso, zIMC } } = data;

      setFormData((prev) => ({
        ...prev,
        pedZScoreTalla: zTalla,
        pedZScorePeso: zPeso,
        pedZScoreIMC: zIMC
      }));
    };

    getCalculos();

  }, [
    esAdulto,
    esPediatrico,
    formData.pesoActual,
    formData.talla,
    visita.fechaNacimiento,
    visita.sexo]
  );

  useEffect(() => {
    //Cálculo y auto selección de Diagnostico Nutricional por ind. Antropométrico para pediátricos
    if (
      esAdulto ||
      !combosRI?.idComboRIValorDiagnostico ||
      !combosRI?.pedMetaSoporteNutricional ||
      isNaN(formData.pedZScoreIMC))
      return;

    const diagnosticos = combosRI.idComboRIValorDiagnostico;
    const metaSoporte = combosRI.pedMetaSoporteNutricional;

    const crecimientoId = metaSoporte.find((x) => x.displayName.includes('Crecimiento'))?.id;
    const replecionId = metaSoporte.find((x) => x.displayName.includes('Repleción'))?.id;

    const normalId = diagnosticos.find((x) => x.displayName.includes('Normal'))?.id;
    const sobrepesoId = diagnosticos.find((x) => x.displayName.includes('Sobrepeso'))?.id;
    const obesidadId = diagnosticos.find((x) => x.displayName.includes('Obesidad'))?.id;
    const desnutricionLeveId = diagnosticos.find((x) => x.displayName.includes('Desnutrición leve'))?.id;
    const desnutricionModeradaId = diagnosticos.find((x) => x.displayName.includes('Desnutrición Moderada'))?.id;
    const desnutricionGraveId = diagnosticos.find((x) => x.displayName.includes('Desnutrición Grave'))?.id;

    const reglasDiagnostico = [
      { id: normalId, meta: crecimientoId, condition: (zimc, edad) => edad >= 0 && edad <= 5 && -1 <= zimc && zimc <= 2 || edad > 5 && edad <= 15 && -1 < zimc && zimc < 1 },
      { id: sobrepesoId, meta: crecimientoId, condition: (zimc, edad) => edad >= 0 && edad <= 5 && 2 < zimc && zimc <= 3 || edad > 5 && edad <= 15 && 1 <= zimc && zimc <= 2 },
      { id: obesidadId, meta: crecimientoId, condition: (zimc, edad) => edad >= 0 && edad <= 5 && zimc > 3 || edad > 5 && edad <= 15 && zimc > 2 },
      { id: desnutricionLeveId, meta: replecionId, condition: (zimc) => -2 <= zimc && zimc < -1 },
      { id: desnutricionModeradaId, meta: replecionId, condition: (zimc) => -3 <= zimc && zimc < -2 },
      { id: desnutricionGraveId, meta: replecionId, condition: (zimc) => zimc < -3 }];


    const selectDiagnostico = (zimc, edad) => {
      const regla = reglasDiagnostico.find((r) => r.condition(zimc, edad));
      return regla ? [regla.id, regla.meta] : null;
    };
    const [diagnosticoId, metaSoporteId] = selectDiagnostico(formData.pedZScoreIMC, visita.edad);

    setFormData((prevDatos) => ({
      ...prevDatos,
      idComboRIValorDiagnostico: diagnosticoId,
      pedMetaSoporteNutricional: metaSoporteId
    }));

  }, [
    esAdulto,
    combosRI?.idComboRIValorDiagnostico,
    combosRI?.pedMetaSoporteNutricional,
    formData.pedZScoreIMC,
    visita.edad]
  );

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "datosClinicos",
      stepName: "Datos clínicos",
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  return (
    <CardBox title="Datos clínicos / Parámetros antropométricos">
      <Grid container spacing={2}>
        {formattedInputs.
          filter((input) => !input.hidden).
          map((input) =>
            <Grid size={{ xs: 12 }} key={input.name}>
              {input.name !== 'listaDiagnostico' && <RenderInputByTypeMobile
                input={input.name === 'idComboRIValorAduDiagnosticoNutricional' ? {
                  ...input,
                  options: comboDiagnosticoNutricional
                } : input}
                label={input.displayName}
                value={formData[input.name]}
                handleChange={handleChange}
                row={input.type === "multiple_boolean"} />
              }
              {input.name === 'listaDiagnostico' && <SearchDiagnosticoCIE
                diagnosticosRIModel={formData.listaDiagnostico}
                handleChange={handleChange}
                inputName={input.name}
                readOnly={readOnly} />
              }
            </Grid>
          )}
      </Grid>
    </CardBox>);

}

const inputs = [
  // Comunes
  {
    name: "listaDiagnostico",
    displayName: "Diagnóstico CIE-10",
    type: "custom",
    required: true
  },
  {
    name: "oncologico",
    displayName: "Enf. que motiva el soporte es oncológico",
    type: "boolean",
    lightTitle: true,
    required: true
  },
  {
    name: "pesoActual",
    displayName: "Peso actual(Kg)",
    type: "float",
    min: 1,
    max: 350,
    required: true
  },
  {
    name: "justificacionVariacion",
    displayName: "Justificación variación de peso",
    type: "long_string",
    hidden: true
  },
  {
    name: "talla",
    displayName: "Talla (m)",
    type: "float",
    min: 0,
    max: 3,
    required: true
  },
  {
    displayName: 'Alergias Relevantes',
    name: 'alergiasDatosClinicos',
    type: 'multiple_select',
    options: [],
    required: true
  },
  {
    displayName: 'Otras alergias',
    name: 'descripcionAlergiaOtro',
    type: 'long_string',
    required: true
  },

  // Adultos
  {
    name: "idComboRIValorTiempo",
    displayName: "Periodo de pérdida de peso",
    comboName: 'Tiempo',
    type: "select",
    esAdulto: true,
    required: true
  },
  {
    name: "pesoHabitual",
    displayName: "Peso habitual (Kg)",
    type: "float",
    esAdulto: true,
    required: true
  },
  {
    name: "idComboRIValorEvaluado",
    displayName: "Evaluado",
    comboName: 'Evaluado',
    type: "select",
    esAdulto: true,
    required: true
  },
  {
    name: "iMC",
    displayName: "IMC",
    type: "float",
    esAdulto: true,
    disabled: true,
    required: true
  },
  {
    name: "perdidaPeso",
    displayName: "% Pérdida de peso",
    type: "float",
    esAdulto: true,
    disabled: true,
    required: true
  },
  {
    name: "idComboRIValorGradoDesnutricion",
    displayName: "Grado de desnutrición",
    comboName: 'GradoDesnutricion',
    type: "select",
    esAdulto: true,
    required: true
  },
  {
    name: "idComboRIValorDiagnostico",
    displayName: "Diagnóstico Nutricional",
    type: "select",
    comboName: 'Diagnóstico nutricional',
    esAdulto: true,
    required: true
  },
  {
    name: "idComboRIValorSoporte",
    displayName: "Meta del soporte Nutricional",
    comboName: 'Meta del soporte nutricional',
    type: "select",
    esAdulto: true,
    required: true
  },

  // Pediátricos
  {
    name: "pedZScoreTalla",
    displayName: "Z Score Talla",
    type: "float",
    esPediatrico: true,
    disabled: true,
    required: true
  },
  {
    name: "pedZScorePeso",
    displayName: "Z Score Peso",
    type: "float",
    esPediatrico: true,
    disabled: true,
    required: true
  },
  {
    name: "pedZScoreIMC",
    displayName: "Z Score IMC",
    type: "float",
    esPediatrico: true,
    disabled: true,
    required: true
  },
  {
    name: "idComboRIValorDiagnostico",
    displayName: "Diagnóstico nutricional por Ind. Antropométricos",
    comboName: 'Diagnóstico nutricional por ind. Antropométricos',
    type: "select",
    esPediatrico: true,
    required: true
  },
  {
    name: "idComboRIValorSoporte",
    displayName: "Meta del soporte Nutricional",
    comboName: 'Meta del soporte nutricional',
    type: "select",
    esPediatrico: true,
    required: true
  },

  // Comunes extra
  {
    name: "idComboRIValorJustificacion",
    displayName: "Justificación de soporte",
    comboName: "Justificación de soporte",
    type: "select",
    lightTitle: true,
    required: false
  },
  {
    name: "causaJustificacion",
    displayName: "Causa de la justificación soporte",
    required: true,
    type: "long_string"
  },
  {
    name: "idComboRIValorFase",
    displayName: "Fase del Paciente",
    comboName: 'Fase del paciente Inicio',
    type: "select",
    required: true
  },
  {
    name: "justificacionModificacionDatosClinicos",
    displayName: "Justificación de modificación",
    type: "long_string"
  }];